'use strict'

// A column can set its own value on every save. Rules are ordered. The first
// match wins. Always sets the value on every save. Only when sets it when the
// conditions match, and otherwise leaves what was sent.
//
// The value can be typed, copied from another column, or copied from a field
// on a related record. The server fills `lookups` before this runs.

const { matchesDisplayCondition } = require('./display-rules')
const { isEmptyColumnValue } = require('./column-write')

const BLOCKED_TYPES = new Set(['formula', 'password', 'file', 'image', 'relationship', 'signature', 'sequence'])

function columnAcceptsValueRule (column) {

  return !!(column && column.type && !BLOCKED_TYPES.has(column.type))
}

function relatedKey (spec) {

  if (!spec) return ''

  if (spec.direction === 'reverse') {

    return `reverse:${spec.sourceSpreadsheet || ''}:${spec.sourceRelationshipColumn || ''}:${spec.relatedField || ''}`
  }

  return `forward:${spec.relationshipColumn || ''}:${spec.relatedField || ''}`
}

function dataTypeForColumn (column) {

  if (column && (column.type === 'number' || column.type === 'duration' || column.type === 'sequence')) return 'number'
  if (column && column.type === 'dateTime') return 'datetime'
  if (column && column.type === 'signature') return 'signature'

  return 'string'
}

function normalizeCondition (condition) {

  const source = condition && condition.source === 'field' ? 'field' : 'self'

  return {
    source,
    field: source === 'field' ? String(condition.field || '') : '',
    operator: condition && condition.operator ? String(condition.operator) : '=',
    dataType: condition && condition.dataType ? String(condition.dataType) : '',
    valueSource: condition && condition.valueSource === 'field' ? 'field' : 'literal',
    valueField: condition && condition.valueSource === 'field' ? String(condition.valueField || '') : '',
    value: condition && condition.valueSource === 'field' ? '' : (condition ? condition.value : '')
  }
}

function normalizeValueSource (rule) {

  const source = rule && (rule.valueSource === 'field' || rule.valueSource === 'related') ? rule.valueSource : 'static'
  const direction = rule && rule.direction === 'reverse' ? 'reverse' : 'forward'

  return {
    valueSource: source,
    staticValue: source === 'static' && rule && rule.staticValue != null ? rule.staticValue : '',
    field: source === 'field' ? String(rule.field || '') : '',
    direction,
    relationshipColumn: source === 'related' && direction === 'forward' ? String(rule.relationshipColumn || '') : '',
    relatedField: source === 'related' ? String(rule.relatedField || '') : '',
    sourceSpreadsheet: source === 'related' && direction === 'reverse' ? String(rule.sourceSpreadsheet || '') : '',
    sourceRelationshipColumn: source === 'related' && direction === 'reverse' ? String(rule.sourceRelationshipColumn || '') : ''
  }
}

function normalizeValueRules (input) {

  const source = input && Array.isArray(input.rules) ? input.rules : (Array.isArray(input) ? input : [])

  return {
    rules: source.filter((rule) => rule && typeof rule === 'object').map((rule, index) => Object.assign({
      id: rule.id ? String(rule.id) : `value_${index + 1}`,
      mode: rule.mode === 'onlyWhen' ? 'onlyWhen' : 'always',
      conditionMatchMode: rule.conditionMatchMode === 'any' ? 'any' : 'all',
      conditions: rule.mode === 'onlyWhen' && Array.isArray(rule.conditions) ? rule.conditions.map(normalizeCondition) : []
    }, normalizeValueSource(rule)))
  }
}

function columnValueRules (column) {

  if (!columnAcceptsValueRule(column) || !column.valueRules) return []

  return normalizeValueRules(column.valueRules).rules
}

function valueRuleLookups (columns) {

  const seen = new Set()
  const specs = []

  function add (spec) {

    const key = relatedKey(spec)

    if (!key || seen.has(key)) return

    seen.add(key)
    specs.push(Object.assign({ key }, spec))
  }

  for (const column of columns || []) {

    for (const rule of columnValueRules(column)) {

      if (rule.valueSource !== 'related') continue

      add({
        direction: rule.direction,
        relationshipColumn: rule.relationshipColumn,
        relatedField: rule.relatedField,
        sourceSpreadsheet: rule.sourceSpreadsheet,
        sourceRelationshipColumn: rule.sourceRelationshipColumn
      })
    }
  }

  return specs
}

function conditionMatches (condition, column, values) {

  if (!condition) return false

  const subject = condition.source === 'field' ? values[condition.field] : values[column.uniqueTitle]
  const expected = condition.valueSource === 'field' ? values[condition.valueField] : condition.value
  const dataType = condition.dataType || dataTypeForColumn(condition.source === 'self' ? column : null)

  if (isEmptyColumnValue(subject) && condition.operator !== 'empty' && condition.operator !== 'notEmpty') return false

  return matchesDisplayCondition(subject, condition.operator, expected, dataType)
}

function ruleMatches (rule, column, values) {

  if (!rule) return false
  if (rule.mode !== 'onlyWhen') return true

  const list = rule.conditions || []

  if (!list.length) return false

  const flags = list.map((condition) => conditionMatches(condition, column, values))

  return rule.conditionMatchMode === 'any' ? flags.some(Boolean) : flags.every(Boolean)
}

function assignedValue (column, rule, values, lookups) {

  let raw = ''

  if (rule.valueSource === 'field') raw = values[rule.field]
  else if (rule.valueSource === 'related') raw = lookups && lookups[relatedKey(rule)]
  else raw = rule.staticValue

  if (column.type === 'number') {

    if (raw == null || raw === '') return null

    const number = Number(raw)

    return Number.isFinite(number) ? number : null
  }

  if (raw == null) return ''

  return raw
}

function sameValue (left, right) {

  if (left == null || left === '') return right == null || right === ''
  if (right == null || right === '') return false

  return String(left) === String(right)
}

function applyColumnValueRules (columns, values, lookups) {

  const sent = Object.assign({}, values || {})
  const replacements = []

  for (const column of columns || []) {

    if (!column || !column.uniqueTitle) continue

    let matched = null

    for (const rule of columnValueRules(column)) {

      if (!ruleMatches(rule, column, sent)) continue

      matched = rule
      break
    }

    if (!matched) continue

    const next = assignedValue(column, matched, sent, lookups)
    const previous = sent[column.uniqueTitle]

    if (sameValue(previous, next)) continue

    values[column.uniqueTitle] = next
    replacements.push({
      uniqueTitle: column.uniqueTitle,
      title: column.title || column.uniqueTitle,
      from: previous == null ? '' : previous,
      to: next == null ? '' : next,
      ruleId: matched.id
    })
  }

  return replacements
}

function recordForResponse (record) {

  const plain = record && record.toJSON ? record.toJSON() : Object.assign({}, record || {})
  const list = record && record.columnValueReplacements

  if (list && list.length) plain.columnValueReplacements = list

  return plain
}

module.exports = {
  columnAcceptsValueRule,
  relatedKey,
  normalizeValueRules,
  valueRuleLookups,
  applyColumnValueRules,
  recordForResponse
}
